import { Tag, TagProps } from '@scalingworks/react-admin-ui';
import { createHelpers, createResource, ResourceField } from '@scalingworks/refine-react-admin';
import { type ChallengeSubmission, ChallengeSubmissionStatus } from '~/api';
import { resourceNames } from '~/resources/resource-names';
import { ResourceContext } from '~/resources/type';
import { FiCheckCircle } from 'react-icons/fi';
import { ChallengeSubmissionShowPage } from './show';
import dayjs from 'dayjs';
import { FullDateTimeFormat } from '~/config/constant';
import { formatFullName } from '../helpers';

const { defineFields, defineShowPage } = createHelpers<ChallengeSubmission>({
  resourceName: resourceNames.challengeSubmission,
});

export const ChallengeStatusColor: Record<ChallengeSubmissionStatus, TagProps['color']> = {
  [ChallengeSubmissionStatus.Approved]: 'success',
  [ChallengeSubmissionStatus.Pending]: 'warning',
  [ChallengeSubmissionStatus.Rejected]: 'error',
};

export const challengeSubmissionFields: ResourceField<ChallengeSubmission>[] = [
  'id',
  'createdAt',
  'updatedAt',
  'status',
  'approvedDate',
  'rejectedDate',
  'adminReason',
  'imageUrls',
  'internalRemark',
  'metadata',
  { challenge: ['id', 'title'] },
  { customer: ['id', 'firstName', 'lastName', 'phoneNumber'] },
  { admin: ['id', 'firstName', 'lastName'] },
] as const;

export const challengeSubmissionResource = (context: ResourceContext) => {
  return createResource({
    name: resourceNames.challengeSubmission,
    label: 'Challenge Submission',
    icon: <FiCheckCircle />,
    fields: defineFields(challengeSubmissionFields),
    defaultValues: {} as any,
    defaultPageSize: 25,
    defaultSorter: [{ field: 'createdAt', order: 'desc' }],
    allowEdit: false,
    allowCreate: false,
    allowDelete: false,
    searchConfig: {
      placeholder: ({ t }) =>
        t('challengeSubmissions.placeholder.search', {
          fallback: 'Search submissions',
          ns: 'common',
        }),
    },
    filterControls: {
      createdAt: {
        type: 'daterange',
        config: {
          label: 'Submission Date',
        },
      },
      status: {
        type: 'select',
        operator: 'eq',
        config: {
          placeholder: 'Select status',
          label: 'Status',
          options: Object.values(ChallengeSubmissionStatus).map((status) => ({
            value: status,
            label: status,
          })),
        },
      },
    },
    filterConfig: {
      alwaysExpanded: true,
    },
    columns: ({ LinkToDetails, t }) => [
      {
        id: 'challenge',
        header: t('challengeSubmissions.column.challenge', { fallback: 'Challenge', ns: 'common' }),
        cell: (data: any) => {
          const { id, challenge } = data.row.original;

          return (
            <LinkToDetails resourceId={id}>
              <span>{challenge.title}</span>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'customer',
        header: t('challengeSubmissions.column.customer', { fallback: 'Customer', ns: 'common' }),
        cell: (data: any) => {
          const { id, customer } = data.row.original;

          if (!customer) return '-';

          return (
            <LinkToDetails resourceId={id}>
              <span>{customer ? formatFullName(customer.firstName, customer.lastName) : '-'}</span>
              <span> ({customer?.phoneNumber})</span>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'submittedAt',
        header: t('challengeSubmissions.column.submittedAt', {
          fallback: 'Submitted At',
          ns: 'common',
        }),
        cell: (data: any) => {
          const { id, createdAt } = data.row.original;
          return (
            <LinkToDetails resourceId={id}>
              <span>{dayjs(createdAt).format(FullDateTimeFormat)}</span>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'status',
        header: t('challengeSubmissions.column.status', { fallback: 'Status', ns: 'common' }),
        cell: (data: any) => {
          const { id, status } = data.row.original;
          return (
            <LinkToDetails resourceId={id}>
              <Tag color={ChallengeStatusColor[status as ChallengeSubmissionStatus] || 'default'}>
                {t(`challengeSubmissions.status.${status?.toLowerCase() || 'unknown'}`, {
                  fallback: status || 'Unknown',
                  ns: 'common',
                }).toUpperCase()}
              </Tag>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'reviewedBy',
        header: t('challengeSubmissions.column.reviewedBy', {
          fallback: 'Reviewed By',
          ns: 'common',
        }),
        cell: (data: any) => {
          const { id, admin } = data.row.original;
          return (
            <LinkToDetails resourceId={id}>
              <span>{admin ? formatFullName(admin.firstName, admin.lastName) : '-'}</span>
            </LinkToDetails>
          );
        },
      },
    ],
    show: defineShowPage({
      component: () => <ChallengeSubmissionShowPage />,
    }),
  });
};
